import { zodResolver } from "@hookform/resolvers/zod"
import { useNavigate } from "@tanstack/react-router"
import { differenceInYears, isAfter, parseISO } from "date-fns"
import { useCallback, useEffect, useMemo, useRef } from "react"
import { useForm } from "react-hook-form"
import { z } from "zod"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { DatePicker } from "@/components/ui/date-picker"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import {
  useIsSavingMembershipApplicationDraft,
  useMembershipApplicationDraft,
  useSaveMembershipApplicationDraft,
  type MembershipApplicationProfileDraft,
} from "../api/useMembershipApplicationDraft"
import { useApplicationSectionConfiguration } from "../hooks/useApplicationConfiguration"

const profileFormSchema = z.object({
  firstName: z
    .string()
    .trim()
    .min(2, "Enter your first name")
    .max(80, "First name is too long"),
  middleName: z
    .string()
    .trim()
    .max(80, "Middle name is too long")
    .optional(),
  organisation: z
    .string()
    .trim()
    .min(2, "Enter your organisation")
    .max(160, "Organisation name is too long"),
  lastName: z
    .string()
    .trim()
    .min(2, "Enter your surname")
    .max(80, "Surname is too long"),
  email: z
    .string()
    .trim()
    .email("Enter a valid email address")
    .max(160, "Email address is too long"),
  phoneNumber: z
    .string()
    .trim()
    .min(7, "Enter a valid phone number")
    .max(20, "Phone number is too long")
    .regex(/^[+0-9()\s-]+$/, "Use digits, spaces, parentheses, plus or hyphen characters"),
  dateOfBirth: z
    .string()
    .min(1, "Select your date of birth")
    .refine((value) => {
      const parsed = parseISODate(value)
      return parsed !== null && !isAfter(parsed, new Date())
    }, "Enter a valid date of birth")
    .refine((value) => {
      const parsed = parseISODate(value)
      if (parsed === null) return false
      return differenceInYears(new Date(), parsed) >= 17
    }, "You must be at least 17 years old to apply"),
  gender: z.enum(["MALE", "FEMALE"]).optional(),
  profileLinkUrl: z
    .string()
    .trim()
    .max(1000, "Profile link is too long")
    .refine((value) => value === "" || z.string().url().safeParse(value).success,
      "Enter a valid URL (e.g. https://linkedin.com/in/yourname)")
    .optional(),
})

export type ProfileFormValues = z.infer<typeof profileFormSchema>

function parseISODate(value: string): Date | null {
  if (!value) return null
  const parsed = parseISO(value)
  if (Number.isNaN(parsed.getTime())) {
    return null
  }
  return parsed
}

function mapDraftToFormValues(profile?: MembershipApplicationProfileDraft): ProfileFormValues {
  return {
    firstName: profile?.firstName ?? "",
    middleName: profile?.middleName ?? "",
    lastName: profile?.lastName ?? "",
    email: profile?.email ?? "",
    phoneNumber: profile?.phoneNumber ?? "",
    dateOfBirth: profile?.dateOfBirth ?? "",
    gender: profile?.gender,
    organisation: profile?.organisation ?? "",
    profileLinkUrl: profile?.profileLinkUrl ?? "",
  }
}

function mapFormValuesToDraft(values: ProfileFormValues): MembershipApplicationProfileDraft {
  return {
    firstName: values.firstName.trim(),
    middleName: values.middleName?.trim() ? values.middleName.trim() : undefined,
    lastName: values.lastName.trim(),
    email: values.email.trim(),
    phoneNumber: values.phoneNumber.trim(),
    dateOfBirth: values.dateOfBirth,
    gender: values.gender,
    organisation: values.organisation.trim(),
    profileLinkUrl: values.profileLinkUrl?.trim() || undefined,
  }
}

export function ProfileStep() {
  const navigate = useNavigate()
  const { data: draft } = useMembershipApplicationDraft()
  const saveDraftMutation = useSaveMembershipApplicationDraft()
  const isSavingDraft = useIsSavingMembershipApplicationDraft()


  const formDefaultValues = useMemo(() => mapDraftToFormValues(draft?.profile), [draft?.profile])

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: formDefaultValues,
    mode: "onChange",
  })
  const { nextStep, section: profileSection } = useApplicationSectionConfiguration(
    "profile"
  )

  useEffect(() => {
    form.reset(formDefaultValues)
  }, [formDefaultValues, form])

  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const skipNextAutosaveRef = useRef(true)

  useEffect(() => {
    skipNextAutosaveRef.current = true
  }, [formDefaultValues])

  useEffect(() => {
    const subscription = form.watch((values, info) => {
      if (skipNextAutosaveRef.current) {
        skipNextAutosaveRef.current = false
        return
      }

      if (info?.type !== "change" && info?.type !== "blur") {
        return
      }

      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current)
      }

      autosaveTimerRef.current = setTimeout(() => {
        const parsed = profileFormSchema.safeParse(values)
        if (!parsed.success) {
          return
        }

        const payload = mapFormValuesToDraft(parsed.data)
        saveDraftMutation.mutate({ profile: payload })
      }, 800)
    })

    return () => {
      subscription.unsubscribe()
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current)
      }
    }
  }, [form, saveDraftMutation])

  const nextStepPath = nextStep?.path

  const handleSubmit = useCallback(
    async (values: ProfileFormValues) => {
      const payload = mapFormValuesToDraft(values)
      await saveDraftMutation.mutateAsync({
        profile: payload,
      })

      if (nextStepPath) {
        navigate({ to: nextStepPath })
      }
    },
    [navigate, nextStepPath, saveDraftMutation],
  )

  const isContinueDisabled = saveDraftMutation.isPending || isSavingDraft

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8">
       
        <Card>
          <CardHeader>
            <CardTitle>Personal information</CardTitle>
            <CardDescription>
              {profileSection?.guidance ?? "We will use these details to create or update your membership record."}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel required>First name</FormLabel>
                    <FormControl>
                      <Input placeholder="Given name" data-invalid={fieldState.invalid} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="lastName"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel required>Surname</FormLabel>
                    <FormControl>
                      <Input placeholder="Family name" data-invalid={fieldState.invalid} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="middleName"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>Middle name (optional)</FormLabel>
                    <FormControl>
                      <Input placeholder="Other names" data-invalid={fieldState.invalid} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="gender"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Gender (optional)</FormLabel>
                    <FormControl>
                      <Select
                        value={field.value ?? undefined}
                        onValueChange={(value) => {
                          field.onChange(value as ProfileFormValues['gender'])
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select gender" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="MALE">Male</SelectItem>
                          <SelectItem value="FEMALE">Female</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="organisation"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel required>Organisation</FormLabel>
                  <FormControl>
                    <Input placeholder="Current organisation" data-invalid={fieldState.invalid} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="profileLinkUrl"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>LinkedIn profile link (optional)</FormLabel>
                  <FormControl>
                    <Input
                      type="url"
                      placeholder="https://linkedin.com/in/yourname"
                      data-invalid={fieldState.invalid}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="email"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel required>Email address</FormLabel>
                    <FormControl>
                      <Input type="email" autoComplete="email" placeholder="you@example.com" data-invalid={fieldState.invalid} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phoneNumber"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel required>Phone number</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g. +256 700 000000"
                        data-invalid={fieldState.invalid}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="dateOfBirth"
                render={({ field }) => {
                  const maxDate = new Date()
                  maxDate.setFullYear(maxDate.getFullYear() - 17)
                  return (
                    <FormItem>
                      <FormLabel required>Date of birth</FormLabel>
                      <FormControl>
                        <DatePicker
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="Select date of birth"
                          disableFuture
                          maxDate={maxDate}
                          minDate={new Date("1900-01-01")}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )
                }}
              />
            </div>
          </CardContent>
          <CardFooter className="flex justify-end">
            <Button type="submit" disabled={isContinueDisabled}>
              {isContinueDisabled ? "Saving..." : "Continue"}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </Form>
  )
}
